import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentType";
import { Dropdown } from "primereact/dropdown";
import { TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React, { useState, useEffect, ChangeEvent } from "react";
import {
  WeatherMonitoringProps,
} from "../charts/interfaces";

interface LineOptions {
  color: string;
  width: number;
  dash: string;
}

const lineOptions: { [key: string]: LineOptions } = {
  temperature: { color: "#FF0000", width: 2, dash: "solid" },
  feelsLikeTemperature: { color: "#FF6666", width: 2, dash: "dot" },
  humidity: { color: "#52B1D2", width: 2, dash: "solid" },
  windSpeed: { color: "#0000FF", width: 2, dash: "dot" },
  windDirection: { color: "#0000FF", width: 2, dash: "solid" },
  precipitation: { color: "#00b899", width: 2, dash: "solid" },
  gusture: { color: "#FFA500", width: 2, dash: "dot" },
};

const overriddenTitles: { [key: string]: string } = {
  temperature: "Temperature",
  feelsLikeTemperature: "Feels Like Temperature",
  humidity: "Humidity",
  windSpeed: "Wind Speed",
  windDirection: "Wind Direction",
  precipitation: "Precipitation",
  gusture: "Gusture",
};

type WeatherMonitoringKey = keyof WeatherMonitoringProps;

const defaultConfig: WeatherMonitoringProps = {
  timeInterval: TimeOptions.DAYS.value,
  location: "",
  temperature: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.temperature,
      overriddenTitle: overriddenTitles.temperature,
    },
  },
  feelsLikeTemperature: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.feelsLikeTemperature,
      overriddenTitle: overriddenTitles.feelsLikeTemperature,
    },
  },
  humidity: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.humidity,
      overriddenTitle: overriddenTitles.humidity,
    },
  },
  windSpeed: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.windSpeed,
      overriddenTitle: overriddenTitles.windSpeed,
    },
  },
  windDirection: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.windDirection,
      overriddenTitle: overriddenTitles.windDirection,
    },
  },
  precipitation: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.precipitation,
      overriddenTitle: overriddenTitles.precipitation,
    },
  },
  gusture: {
    isVisible: false,
    dataset: {
      identifier: "",
      line: lineOptions.gusture,
      overriddenTitle: overriddenTitles.gusture,
    },
  },
  dateSelection: { isVisible: false },
};

function WeatherMonitoringEditor({
  config,
  onConfigChange,
}: DashboardEditorProps<WeatherMonitoringProps>) {
  const [currentConfig, setCurrentConfig] = useState<WeatherMonitoringProps>({
    ...defaultConfig,
    ...config,
  });

  useEffect(() => {
    setCurrentConfig({ ...defaultConfig, ...config });
  }, [config]);

  const handleSwitchChange = (name: WeatherMonitoringKey, value: boolean) => {
    const currentValue = currentConfig[name];
      const updatedConfig: WeatherMonitoringProps = {
        ...currentConfig,
        [name]: {
          ...currentValue,
          isVisible: value,
        },
      };
      setCurrentConfig(updatedConfig);
      onConfigChange(updatedConfig);
    
  };

  const handleDatasetChange = (e: string | null, name: WeatherMonitoringKey) => {
    const currentValue = currentConfig[name];
      const updatedConfig: WeatherMonitoringProps = {
        ...currentConfig,
        [name]: {
          ...currentValue,
          dataset: {
            identifier: e ?? "",
            line: lineOptions[name] || { color: "#FF0000", width: 2, dash: "solid" },
            overriddenTitle: overriddenTitles[name] || name,
          },
        },
      };
      setCurrentConfig(updatedConfig);
      onConfigChange(updatedConfig);
  };

  const handleLocationChange = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedConfig: WeatherMonitoringProps = {
      ...currentConfig,
      location: e.target.value,
    };
    setCurrentConfig(updatedConfig);
    onConfigChange(updatedConfig);
  };

  return (
    <div>
      <div className="flex flex-row justify-content-start gap-5">
        <div className="flex flex-column gap-2">
          <label htmlFor="timeInterval">Time Interval</label>
          <Dropdown
            value={currentConfig.timeInterval}
            options={Object.values(TimeOptions).map(option => option.value)}
            onChange={e => {
              const updatedConfig: WeatherMonitoringProps = {
                ...currentConfig,
                timeInterval: e.value,
              };
              setCurrentConfig(updatedConfig);
              onConfigChange(updatedConfig);
            }}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="location">Location</label>
          <InputText
            id="location"
            value={currentConfig.location ?? ""}
            onChange={handleLocationChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="showDateSelection">Show Date Selection</label>
          <InputSwitch
            id="showDateSelection"
            checked={currentConfig.dateSelection?.isVisible ?? false}
            onChange={() =>
              handleSwitchChange("dateSelection", !currentConfig.dateSelection?.isVisible)
            }
          />
        </div>
      </div>
      <div className="grid mt-5">
        {[
          { name: "temperature", label: "Temperature Dataset" },
          { name: "feelsLikeTemperature", label: "Feels Like Temperature Dataset" },
          { name: "humidity", label: "Humidity Dataset" },
          { name: "windSpeed", label: "Wind Speed Dataset" },
          { name: "windDirection", label: "Wind Direction Dataset" },
          { name: "precipitation", label: "Precipitation Dataset" },
          { name: "gusture", label: "Gusture Dataset" },
        ].map(item => {
          const name = item.name as WeatherMonitoringKey;
          const currentValue = currentConfig[name];
          const isVisible =
            currentValue?.isVisible ?? false;

          return (
            <div className="col-4 mb-3" key={name}>
              <div className="flex justify-content-between mb-3">
                <label htmlFor={name}>{item.label}</label>
                <InputSwitch
                  id={name}
                  checked={isVisible}
                  onChange={() => handleSwitchChange(name, !isVisible)}
                />
              </div>
              <DatasetDropdown
                value={
                  currentValue.isVisible
                    ? currentValue.dataset.identifier
                    : ""
                }
                onChange={e => handleDatasetChange(e, name)}
                disabled={!isVisible}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeatherMonitoringEditor;
